import React, { useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  Modal,
  Divider,
  FormControl,
  Typography,
  IconButton,
} from "@mui/material";
import Grid from "@mui/system/Grid";
import { useForm } from "react-hook-form";

import {
  flexAllCentre,
  flexAllEnd,
  flexColumn,
} from "../../../styles/mui/styles/display";

import {
  ButtonCloseStyle,
  ButtonVerifyStyle,
  FaXmarkIcon,
  HorizontalDivider,
} from "../../../app/styles/styles";

import { ButtonContained } from "../../../app/common/button";
import colors from "../../../styles/colors/colors";
import { DropDownPadding } from "../../../components/styles/styles";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";
import { SOC_Modal } from "../style/style";
import { rules } from "../../../utils/validation/Validation";
import { UpdateStartOfCareDatePayload } from "../../../models/Referral";
import { addDaysToDate } from "../../../utils";
import { BCBSMI, BCBSMIMINSOCDATE } from "../../../constants/FunctionalUtils";

export interface NoSocModalProps {
  setSoCModalFlag: (val: boolean) => void;
  SoCModalFlag: boolean;
  currentStartOfCare: string | Date;
  referralId: number;
  healthPlan: string;
  updateSoC: (payload: UpdateStartOfCareDatePayload) => void;
}

interface NoSocForm {
  newStartOfCare: string;
  currentStartOfCare: string;
}

const NoSocModal: React.FC<NoSocModalProps> = ({
  setSoCModalFlag,
  SoCModalFlag,
  referralId,
  healthPlan,
  updateSoC,
  currentStartOfCare,
}) => {
  const currentSoCForRules = String(currentStartOfCare ?? "");

  const minimumSOCDate = currentStartOfCare
    ? healthPlan === BCBSMI
      ? addDaysToDate(BCBSMIMINSOCDATE, 0, "day")
      : addDaysToDate(currentSoCForRules, -5, "day")
    : null;

  const maximumSOCDate = currentStartOfCare
    ? addDaysToDate(currentSoCForRules, 14, "day")
    : null;


  const methods = useForm<NoSocForm>({
    defaultValues: {
      newStartOfCare: "",
      currentStartOfCare: currentStartOfCare
        ? String(currentStartOfCare)
        : "",
    },
  });

  const { handleSubmit, control, reset } = methods;

  useEffect(() => {
    if (SoCModalFlag) {
      reset({
        currentStartOfCare: currentStartOfCare
          ? String(currentStartOfCare)
          : "",
        newStartOfCare: "",
      });
    }
  }, [SoCModalFlag, currentStartOfCare, reset]);

  const onSubmit = (data: NoSocForm) => {
    if (!data.newStartOfCare) {
      return;
    }

    const currentStartOfCareDateObj = dayjs(data.currentStartOfCare);
    const newStartOfCareDateObj = dayjs(data.newStartOfCare);

    if (!currentStartOfCareDateObj.isValid() || !newStartOfCareDateObj.isValid()) {
      return;
    }

    const currentStartOfCareDate = currentStartOfCareDateObj.format("YYYY-MM-DDTHH:mm:ss");
    const newStartOfCareDate = newStartOfCareDateObj.format("YYYY-MM-DDTHH:mm:ss");

    const payload: UpdateStartOfCareDatePayload = {
      referralId,
      currentStartOfCareDate,
      newStartOfCareDate,
    };

    updateSoC(payload);
    setSoCModalFlag(false);
    
  };

  const handleClose = () => {
    setSoCModalFlag(false);
    reset();
  };

  return (
    <Modal
      open={SoCModalFlag}
      aria-labelledby="soc-modal-title"
      sx={{ ...flexAllCentre, zIndex: 1100 }}
    >
      <Box sx={SOC_Modal()}>
        <Grid container mb="0.4rem">
          <Grid size={{ xs: 11.5 }}>
            <Typography id="soc-modal-title" variant="h1">
              Update Start of Care (SoC) Date
            </Typography>
          </Grid>
          <Grid size={{ xs: 0.5 }}>
            <IconButton onClick={handleClose}>
              <FaXmarkIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Divider sx={HorizontalDivider} />

        <Box mb="2rem">
          <Grid container>
            <Grid size={{ xs: 8 }} mb={1} sx={DropDownPadding}>
              <Box mb={1} sx={flexColumn}>
                <FormControl variant="standard">
                  <DatePicker
                    name="currentStartOfCare"
                    control={control}
                    label="Current Start of Care (SoC) Date"
                    manualEntryNotAllowed={true}
                    allowFutureDate = {true}
                    disabled={true}
                  />
                </FormControl>  
              </Box>
            </Grid>
            <Grid size={{ xs: 8 }} mb={1} sx={DropDownPadding}>
              <Box mb={1} sx={flexColumn}>
                <FormControl variant="standard">
                  <DatePicker
                    name="newStartOfCare"
                    control={control}
                    label="New Start of Care (SoC) Date"
                    helper={rules.socDateTimeRequired}
                    allowFutureDate={true}
                    minimumDate={minimumSOCDate}
                    maximumdate={maximumSOCDate}
                  />
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        <Box pt={0}>
    <Box
      component="span"
      sx={{
        fontWeight: "700",
        fontSize: "0.875rem",
        marginRight: "0.5rem",
      }}
    >
      Note:
    </Box>

    <Typography
      component="span"
      variant="h6"
      sx={{ color: colors.fonts[1] }}
    >
      Updating the start of care date can only be completed once and
      will adjust the authorization dates.
    </Typography>
  </Box>
        </Box>

        {/* Footer */}
        <Divider sx={HorizontalDivider} />
        <Grid container>
          <Grid size={{ xs: 12 }} sx={flexAllEnd}>
            <ButtonContained
              id="btnSaveSoc"
              onClick={handleSubmit(onSubmit)}
              label="Save"
              disabled={false}
              style={ButtonVerifyStyle}
            />

            <ButtonContained
              id="btnCloseSoc"
              onClick={handleClose}
              label="Cancel"
              disabled={false}
              style={ButtonCloseStyle}
            />
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default NoSocModal;
